import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { BUSINESS } from "../config/business";

const LegalShell = ({ eyebrow, title, effective, children, testid }) => (
  <div data-testid={testid} className="min-h-screen bg-[#DCCFB9] text-[#2A1C15]">
    <header className="bg-[#0E1C15] border-b border-[rgba(42,28,21,0.18)] px-6 py-4 sticky top-0 z-30 flex items-center justify-between">
      <Link to="/" className="font-heading text-[#6E5022] text-2xl tracking-tight">{BUSINESS.shortName}</Link>
      <Link to="/login" data-testid="legal-back-link" className="inline-flex items-center gap-1 text-[#F2EBDD] hover:text-[#6E5022] font-body tracking-widest text-xs uppercase">
        <ArrowLeft size={14} /> Back to Login
      </Link>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
      <div className="bj-label mb-2">{eyebrow}</div>
      <h1 className="font-heading text-[#14281E] text-5xl leading-none mb-3">{title}</h1>
      <div className="text-[#5E4D40] text-xs tracking-widest uppercase mb-10">
        Effective Date: {effective}
      </div>
      <article className="legal-body text-[#2A1C15] text-sm leading-relaxed">{children}</article>
      <div className="mt-16 pt-6 border-t border-[rgba(42,28,21,0.18)] flex flex-col sm:flex-row justify-between gap-3 text-[0.75rem] text-[#5E4D40] tracking-widest uppercase">
        <span>© 2026 {BUSINESS.legalName}. All rights reserved.</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-[#6E5022]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#6E5022]">Terms</Link>
        </div>
      </div>
    </main>

    <style>{`
      .legal-body h2 { font-family:'Cormorant Garamond',serif; font-weight:600; color:#14281E; font-size:1.8rem; margin: 2.25rem 0 .75rem; letter-spacing:-0.01em; }
      .legal-body h3 { font-family:'Cormorant Garamond',serif; font-weight:600; color:#14281E; font-size:1.25rem; margin: 1.5rem 0 .5rem; }
      .legal-body p { margin: 0 0 .85rem; color:#2A1C15; }
      .legal-body ul { margin: .5rem 0 1rem 1.25rem; }
      .legal-body li { margin: .35rem 0; color:#2A1C15; }
      .legal-body strong { color:#14281E; font-weight:600; }
      .legal-body a { color:#14281E; text-decoration: underline; text-decoration-color: #6E5022; text-underline-offset: 3px; text-decoration-thickness: 1.5px; }
      .legal-body a:hover { color:#6E5022; }
      .legal-body .summary { background:#E7DDCB; border-left:3px solid #6E5022; padding:14px 18px; margin:0 0 1.5rem; color:#2A1C15; border-radius:0 6px 6px 0; }
    `}</style>
  </div>
);

export const PrivacyPage = () => (
  <LegalShell testid="privacy-page" eyebrow="Legal" title="Privacy Policy" effective="June 22, 2026">
    <p>{BUSINESS.legalName} (&ldquo;JC3,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates <a href={BUSINESS.websiteUrl} target="_blank" rel="noreferrer">{BUSINESS.website}</a>. This Privacy Policy explains how we collect, use, disclose, and protect your information.</p>

    <h2>Information We Collect</h2>
    <ul>
      <li>Name, email, and phone collected from forms you submit</li>
      <li>Business information shared during consultation</li>
      <li>Payment information processed through a secure payment processor (we do not store card numbers)</li>
      <li>IP address, browser type, and device information collected via Google Analytics 4</li>
      <li>Cookies (see below)</li>
    </ul>

    <h2>How We Use Your Information</h2>
    <ul>
      <li>Service delivery and client support</li>
      <li>Communications about your account, services, and updates</li>
      <li>Payment processing</li>
      <li>Marketing (opt-out available at any time)</li>
      <li>Site analytics and product improvement</li>
      <li>Legal and regulatory compliance</li>
    </ul>

    <h2>We Do Not Sell Personal Information</h2>
    <p>We do not sell, rent, or trade your personal information. We work with the following service providers under data-processing agreements: <strong>Airtable, Resend, PaymentCloud, Authorize.net, Google Analytics, and GoHighLevel</strong>.</p>

    <h2>Cookies</h2>
    <p>We use Google Analytics 4 with anonymized tracking. You can opt out of GA tracking using the Google Analytics opt-out browser add-on at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer">tools.google.com/dlpage/gaoptout</a>.</p>

    <h2>Data Retention</h2>
    <p>Client records are retained for a minimum of three (3) years following the end of an engagement, or longer if required by law.</p>

    <h2>Your Rights</h2>
    <ul>
      <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
      <li><strong>Correct</strong> — request correction of inaccurate or incomplete data</li>
      <li><strong>Delete</strong> — request deletion of your personal data (subject to legal obligations)</li>
      <li><strong>Opt out of marketing</strong> — unsubscribe at any time</li>
      <li><strong>Data portability</strong> — request your data in a portable format</li>
    </ul>
    <p>To exercise these rights, contact <a href="mailto:privacy@jc3wealthlegacy.com">privacy@jc3wealthlegacy.com</a>.</p>

    <h2>Security</h2>
    <p>We protect your data with SSL encryption, Authorize.net tokenization for payments, and access controls. We do not collect data from minors under 18.</p>

    <h2>Contact</h2>
    <p><strong>{BUSINESS.legalName}</strong></p>
    <p>{BUSINESS.addressLine1}, {BUSINESS.addressLine2}</p>
    <p>{BUSINESS.city}, {BUSINESS.state} {BUSINESS.postalCode}</p>
    <p>Phone: <a href={`tel:${BUSINESS.phone.replace(/[^0-9]/g, "")}`}>{BUSINESS.phone}</a></p>
    <p>Email: <a href="mailto:privacy@jc3wealthlegacy.com">privacy@jc3wealthlegacy.com</a></p>
    <p>Website: <a href={BUSINESS.websiteUrl} target="_blank" rel="noreferrer">{BUSINESS.website}</a></p>
  </LegalShell>
);

export const TermsPage = () => (
  <LegalShell testid="terms-page" eyebrow="Legal" title="Terms of Service" effective="June 22, 2026">
    <p>These Terms of Service (&ldquo;Terms&rdquo;) govern your use of services provided by {BUSINESS.legalName} (&ldquo;JC3,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By engaging our services or using our websites, you agree to be bound by these Terms.</p>

    <h2>Services</h2>
    <p>JC3 provides the following educational consulting services:</p>
    <ul>
      <li>Financial Profile Development</li>
      <li>Personal Capital Strategy</li>
      <li>Business Capital Strategy</li>
      <li>Account History Positioning</li>
      <li>Grant Readiness & Research</li>
      <li>Business Foundation Setup</li>
    </ul>
    <p>All services are educational and consulting in nature. <strong>JC3 is not a law firm, accounting firm, credit repair organization, or registered investment advisor. JC3 does not access, manage, or place any accounts on behalf of clients.</strong></p>

    <h2>No Guarantees</h2>
    <p>We do not guarantee credit scores, loan approvals, funding amounts, grant awards, or investment returns. Results depend on many factors outside our control.</p>

    <h2>Client Responsibilities</h2>
    <ul>
      <li>Provide accurate and current information</li>
      <li>Follow the action steps recommended in your strategy</li>
      <li>Notify us of significant changes to your financial situation</li>
      <li>Understand that results depend on actions you take</li>
    </ul>

    <h2>Payments</h2>
    <p>Payments are processed via <strong>PaymentCloud</strong> with <strong>Authorize.net</strong> as the gateway. A 3% processing fee applies to all payments. Fees for services rendered are <strong>non-refundable</strong>. Refund policy: see <a href="https://jc3wealthlegacy.com/refund" target="_blank" rel="noreferrer">jc3wealthlegacy.com/refund</a>.</p>

    <h2>Intellectual Property</h2>
    <p>All content, materials, strategies, frameworks, and resources provided by JC3 are the intellectual property of JC3 Wealth Legacy Group LLC and protected by applicable copyright and trademark laws.</p>

    <h2>Prohibited Conduct</h2>
    <ul>
      <li>Unlawful use of our services or websites</li>
      <li>Unauthorized access to any portion of our systems</li>
      <li>Reselling or redistributing our materials without written permission</li>
      <li>Misrepresenting your identity</li>
      <li>Conduct that damages our reputation or operations</li>
    </ul>

    <h2>Limitation of Liability</h2>
    <p>To the maximum extent permitted by law, JC3 is not liable for any indirect or consequential damages. Total liability for any claim is capped at the amount you paid us in the ninety (90) days preceding the claim.</p>

    <h2>Governing Law</h2>
    <p>These Terms are governed by the laws of the State of Georgia.</p>

    <h2>Contact</h2>
    <p><strong>{BUSINESS.legalName}</strong></p>
    <p>{BUSINESS.addressLine1}, {BUSINESS.addressLine2}</p>
    <p>{BUSINESS.city}, {BUSINESS.state} {BUSINESS.postalCode}</p>
    <p>Phone: <a href={`tel:${BUSINESS.phone.replace(/[^0-9]/g, "")}`}>{BUSINESS.phone}</a></p>
    <p>Email: <a href="mailto:legal@jc3wealthlegacy.com">legal@jc3wealthlegacy.com</a></p>
  </LegalShell>
);
